import { Mutation, Query } from "@apollo/client/react/components";
import { Col, Grid, Loader, Row, RowWrap } from "@govlaunch/core";
import * as palette from "@govlaunch/palette";
import { get, getOr, pick } from "lodash/fp";
import { NextPage } from "next";
import { useRouter } from "next/router";
import { default as React } from "react";
import { useIntl } from "react-intl";
import { useSelfie } from "~/components/auth/Auth";
import Cover, { makeImaginaryUrls, PLACEHOLDER_COVER } from "~/components/cover/Cover";
import Feed from "~/components/feed/Feed";
import WriteContentBox from "~/components/feed/WriteContentBox";
import GovernmentActions from "~/components/government/GovernmentActions";
import GovernmentAwards from "~/components/government/GovernmentAwards";
import GovernmentInfo from "~/components/government/GovernmentInfo";
import GovernmentProfileForm from "~/components/government/GovernmentProfileForm";
import GovernmentStories from "~/components/government/GovernmentStories";
import ProjectSharingWithGroups from "~/components/government/ProjectSharingWithGroups";
import RequestAccess from "~/components/government/RequestAccess";
import SharingBox from "~/components/government/SharingBox";
// import Stack from "~/components/government/Stack";
import NewLinkTab from "~/components/profile/NewLinkTab";
import ProductList from "~/components/profile/ProductList";
import ProfileNavbar from "~/components/profile/ProfileNavbar";
import Resources from "~/components/profile/Resources";
import ResourcesForm, {
  ResourcesFormContextConsumer,
  ResourcesFormContextProvider,
} from "~/components/profile/ResourcesForm";
import Team from "~/components/profile/Team";
import FullGovernmentProjects from "~/components/project/FullGovernmentProjects";
import GovernmentPageSeo from "~/components/seo/GovernmentPageSeo";
import { Margin } from "~/components/spacings";
import UpgradeAccountBanner from "~/components/vendor/profile/UpgradeAccountBanner";
import useIsMobile from "~/lib/hooks/useIsMobile";
import SetGovernmentResources from "~/lib/mutations/SetGovernmentResources";
import SetProfileCover from "~/lib/mutations/SetProfileCover";
import {
  ISetGovernmentResourcesMutation,
  ISetGovernmentResourcesMutationVariables,
} from "~/lib/mutations/__generated__/SetGovernmentResources.generated";
import GovernmentQuery from "~/lib/queries/GovernmentQuery";
import { IGovernmentQuery, IGovernmentQueryVariables } from "~/lib/queries/__generated__/GovernmentQuery.generated";
import { ISelfieQuery } from "~/lib/queries/__generated__/SelfieQuery.generated";
import Redirect from "~/lib/route/Redirect";
import mediaQueries from "~/lib/utils/mediaQueries";
import safeGetQueryParameter from "~/lib/utils/safeGetQueryParameter";

import { SigninModal } from "~/src/components/auth/SignInModal";
import NavbarLayout from "~/src/components/navbar/NavbarLayout";
import { IConnectTargetType, IFlatResource, IGovernment, IGovernmentUser, IStory } from "~/types/types";

type Content = "connect" | "projects" | "products" | "stories" | "sharing" | "settings" | "actions";

interface IGovernmentProps {
  government: IGovernmentQuery["government"];
  user: ISelfieQuery["selfie"];
  content: Content;
}

interface IInviteMessage {
  accepted: boolean;
}

interface IGovernmentProfileProps {
  user: ISelfieQuery["selfie"];
  token: string | null;
  projectSlug: string | null;
  governmentSlug: string;
  content: Content;
}

interface IGovernmentPageProps {
  governmentSlug: string;
  token: string | null;
  projectSlug: string | null;
  content: Content;
}

interface IGatedGovernmentProfileProps {
  government: IGovernmentQuery["government"];
  user: ISelfieQuery["selfie"];
}

interface IGovernmentTabs {
  government: NonNullable<IGovernmentQuery["government"]>;
  user: ISelfieQuery["selfie"];
  tab: Content;
}

(global as any).joinedFromToken = null;

const GovernmentPage: NextPage<IGovernmentPageProps> = ({ governmentSlug, token, projectSlug, content }) => {
  const user = useSelfie();
  const router = useRouter();

  if (token && !user) {
    return (
      <SigninModal
        isOpen={true}
        inviteToken={token}
        onRequestClose={() => {
          router.replace(`/governments/${governmentSlug}`);

          (global as any).joinedFromToken = true;
        }}
      />
    );
  }

  return (
    <NavbarLayout>
      <GovernmentProfile
        user={user}
        token={token}
        governmentSlug={governmentSlug}
        projectSlug={projectSlug}
        content={content}
      />
    </NavbarLayout>
  );
};

GovernmentPage.getInitialProps = ({ query }) => {
  return {
    governmentSlug: safeGetQueryParameter(query, "governmentSlug"),
    token: safeGetQueryParameter(query, "token"),
    projectSlug: safeGetQueryParameter(query, "projectSlug"),
    content: safeGetQueryParameter(query, "content") as Content,
  };
};

const GovernmentProfile: React.FunctionComponent<IGovernmentProfileProps> = ({
  user,
  token,
  governmentSlug,
  content,
}) => {
  return (
    <Query<IGovernmentQuery, IGovernmentQueryVariables>
      query={GovernmentQuery}
      variables={{
        governmentSlug,
      }}
    >
      {({ loading, data }) => {
        if (loading) {
          return (
            <div
              css={{
                padding: 30,
                width: "100%",
                textAlign: "center",
              }}
            >
              <Loader centered={true} />
            </div>
          );
        }

        if (!data || !data.government) {
          return <Redirect to="/" />;
        }

        const { government } = data;

        if (token && user) {
          return <Redirect to={`/governments/${governmentSlug}`} />;
        }

        if (user && !user.verified) {
          return <GatedGovernmentProfile government={government} user={user} />;
        }

        return (
          <div
            css={{
              marginTop: 30,
              marginBottom: 30,
              width: "100%",
            }}
            data-intercom-target={government.membersCount > 0 ? null : "Claim Government"}
          >
            <Government government={government} user={user} content={content} />
          </div>
        );
      }}
    </Query>
  );
};

function GatedGovernmentProfile({ government, user }: IGatedGovernmentProfileProps) {
  const isMobile = useIsMobile();

  if (!government || !user) {
    return null;
  }

  return (
    <Margin mt={30} mb={30}>
      <div
        css={{
          maxWidth: 940,
          width: "100%",
          margin: "0 auto",
          padding: "0 15px",
          display: "grid",
          alignItems: "flex-start",
          gridGap: 16,
          gridAutoFlow: isMobile ? "row" : "column",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 2fr",
        }}
      >
        <GovernmentInfo government={government} user={user} />

        {government.viewerCanRequestAccess && <RequestAccess government={government} />}
      </div>
    </Margin>
  );
}

function getTab(
  user: ISelfieQuery["selfie"],
  government: Pick<IGovernment, "membersCount" | "feedCount">,
  content: Content,
): Content {
  if (content) {
    return content;
  }

  if (!user) {
    return "projects";
  }

  if (government.feedCount > 0) {
    return "connect";
  }

  return "projects";
}

const Government = ({ government, user, content }: IGovernmentProps) => {
  const isMobile = useIsMobile();

  if (!government) {
    return null;
  }

  const tab = getTab(user, government, content);

  return (
    <>
      <GovernmentPageSeo government={government} tab={tab} />

      <Grid width={1224}>
        {((government.cover && government.cover.original && user && user.canSeeActiveGovernments) ||
          government.viewerIsMember) && (
          <Row mb={[5, 5, 5, 5]}>
            <Col width={[1, 1, 1, 1]}>
              {(global as any).joinedFromToken && (
                <Margin mb={12}>
                  <InviteMessage
                    accepted={!!(government.members as IGovernmentUser[]).find(({ _id }) => _id === get("_id", user))}
                  />
                </Margin>
              )}

              <div
                css={{
                  position: "relative",
                }}
              >
                <SetProfileCover entityType="Government" targetId={government._id}>
                  {({ onSetProfileCover, onSetRepositionedCover, onRemoveCover }) => (
                    <Cover
                      alt="Cover"
                      height={250}
                      width={1224}
                      originalSource={getOr(null, "cover.original", government)}
                      repositionedSource={getOr(null, "cover.repositioned", government)}
                      placeholderSource={PLACEHOLDER_COVER}
                      onReposition={(originalSource, cropState) => {
                        const repositionedSource = makeImaginaryUrls(originalSource, cropState);
                        onSetRepositionedCover(originalSource, repositionedSource.cropped, cropState);
                      }}
                      onChange={onSetProfileCover}
                      onRemove={onRemoveCover}
                      readOnly={!government.viewerIsMember}
                      containerProps={{
                        style: {
                          borderRadius: 5,
                          overflow: "hidden",
                        },
                      }}
                    />
                  )}
                </SetProfileCover>
              </div>
            </Col>
          </Row>
        )}

        <RowWrap>
          <Col width={[1, 1, 4 / 12, 4 / 12]}>
            <div
              css={{
                display: "grid",
                gridRowGap: 24,
              }}
            >
              <GovernmentInfo government={government} user={user} content={content} />

              {isMobile && user && user.canSeeActiveGovernments && <Navbar government={government} tab={tab} />}

              {user && user.__typename === "GovernmentUser" && (
                <Team
                  members={government.members}
                  viewerIsMember={government.viewerIsMember || false}
                  viewerCanInviteMembers={government.viewerCanInviteMembers || false}
                  viewerCanRemoveMembers={government.viewerCanRemoveMembers || false}
                  viewerCanSeeTeam={government.viewerCanSeeTeam}
                  targetType="GOVERNMENT"
                  targetId={government._id}
                />
              )}

              <ResourcesFormContextProvider>
                <>
                  {user && user.canSeeActiveGovernments && (
                    <ResourcesFormContextConsumer>
                      {({ toggle }) => <GovernmentAwards government={government} onAddLink={toggle} />}
                    </ResourcesFormContextConsumer>
                  )}

                  {government.viewerIsMember && (
                    <Mutation<ISetGovernmentResourcesMutation, ISetGovernmentResourcesMutationVariables>
                      mutation={SetGovernmentResources}
                    >
                      {(setResources) => (
                        <ResourcesForm
                          resources={government.resources as IFlatResource[]}
                          dataIntercomTarget={government.viewerIsMember ? "Government Resources" : null}
                          onAdd={(resource) => {
                            setResources({
                              variables: {
                                governmentId: government._id,
                                resources: [resource, ...(government.resources as IFlatResource[])].map(
                                  pick(["type", "title", "url", "filename", "body", "addedAt"]) as any,
                                ),
                              },
                            });
                          }}
                          onRemove={(resource) => {
                            return setResources({
                              variables: {
                                governmentId: government._id,
                                resources: (government.resources as IFlatResource[])
                                  .filter((r) => r !== resource)
                                  .map(pick(["type", "title", "url", "filename", "body", "addedAt"])) as any,
                              },
                            });
                          }}
                        />
                      )}
                    </Mutation>
                  )}
                </>
              </ResourcesFormContextProvider>

              {!government.viewerIsMember && user && user.canSeeActiveGovernments && (
                <Resources
                  resources={government.resources as IFlatResource[]}
                  dataIntercomTarget={government.viewerIsMember ? "Government Resources" : null}
                />
              )}

              <ResourcesFormContextProvider>
                <>
                  {user && user.canSeeActiveGovernments && (
                    <ResourcesFormContextConsumer>
                      {({ toggle }) => <GovernmentAwards government={government} onAddLink={toggle} />}
                    </ResourcesFormContextConsumer>
                  )}

                  {government.viewerIsMember && (
                    <Mutation<ISetGovernmentResourcesMutation, ISetGovernmentResourcesMutationVariables>
                      mutation={SetGovernmentResources}
                    >
                      {(setResources) => (
                        <ResourcesForm
                          resources={government.resources as IFlatResource[]}
                          dataIntercomTarget={government.viewerIsMember ? "Government Resources" : null}
                          onAdd={(resource) => {
                            setResources({
                              variables: {
                                governmentId: government._id,
                                resources: [resource, ...(government.resources as IFlatResource[])].map(
                                  pick(["type", "title", "url", "filename", "body", "addedAt"]) as any,
                                ),
                              },
                            });
                          }}
                          onRemove={(resource) => {
                            return setResources({
                              variables: {
                                governmentId: government._id,
                                resources: (government.resources as IFlatResource[])
                                  .filter((r) => r !== resource)
                                  .map(pick(["type", "title", "url", "filename", "body", "addedAt"])) as any,
                              },
                            });
                          }}
                        />
                      )}
                    </Mutation>
                  )}
                </>
              </ResourcesFormContextProvider>

              {!government.viewerIsMember && user && user.canSeeActiveGovernments && (
                <Resources
                  resources={government.resources as IFlatResource[]}
                  dataIntercomTarget={government.viewerIsMember ? "Government Resources" : null}
                />
              )}

              {user && user.__typename === "PendingUser" && !user.verified && <RequestAccess government={government} />}
            </div>
          </Col>

          <Col width={[1, 1, 8 / 12, 8 / 12]} mt={[24, 24, 0, 0]}>
            {user && user.__typename === "VendorUser" && !user.canSeeActiveGovernments ? (
              <UpgradeAccountBanner />
            ) : (
              <GovernmentTabs government={government} user={user} tab={tab} />
            )}
          </Col>
        </RowWrap>
      </Grid>
    </>
  );
};

function GovernmentTabs({ government, tab, user }: IGovernmentTabs) {
  const isMobile = useIsMobile();

  return (
    <>
      {!isMobile && <Navbar government={government} tab={tab} />}

      <div
        css={{
          marginTop: 24,
        }}
      >
        {tab === "projects" && <FullGovernmentProjects government={government} />}

        {tab === "stories" && (
          <GovernmentStories government={government} stories={government.stories!.items as IStory[]} />
        )}

        {tab === "connect" && (
          <div
            css={{
              display: "grid",
              gridRowGap: 16,
            }}
          >
            {user &&
              user.__typename === "GovernmentUser" &&
              user.government &&
              user.government._id === government._id && (
                <WriteContentBox
                  user={user}
                  dataIntercomTarget={government.viewerIsMember ? "Government Write Content Box" : null}
                />
              )}
            <Feed targetId={government._id} targetType={IConnectTargetType.Government} />
          </div>
        )}

        {tab === "products" && government.products && (
          <div>
            <ProductList
              products={government.products}
              government={government}
              dataIntercomTarget={government.viewerIsMember ? "Government Products" : null}
            />
          </div>
        )}

        {(tab === "settings" || tab === "sharing") && (
          <div
            css={{
              display: "grid",
              gridRowGap: 28,
            }}
          >
            <GovernmentProfileForm government={government} />

            {government.groups.length > 0 && <ProjectSharingWithGroups government={government} />}

            <SharingBox government={government} />
          </div>
        )}

        {tab === "actions" && <GovernmentActions governmentSlug={government.slug} />}
      </div>
    </>
  );
}

const InviteMessage: React.FunctionComponent<IInviteMessage> = ({ accepted }) => {
  return (
    <div
      css={{
        backgroundColor: accepted ? palette.greenBlue : palette.red,
        padding: 12,
        borderRadius: 4,
      }}
    >
      <p
        css={{
          color: palette.white,
          fontSize: 14,
          margin: 0,
        }}
      >
        {accepted ? (
          <>You're now a member of this Government!</>
        ) : (
          "Could not join this Government because the invite link has expired or is invalid."
        )}
      </p>
    </div>
  );
};

interface INavbarProps {
  government: NonNullable<IGovernmentQuery["government"]>;
  tab: string | null;
}

function Navbar({ government, tab }: INavbarProps) {
  const intl = useIntl();

  return (
    <ProfileNavbar>
      {government.feedCount > 0 && (
        <NewLinkTab
          href="/governments/[governmentSlug]"
          as={`/governments/${government.slug}`}
          title={intl.formatMessage({ defaultMessage: "Activity", id: "ZmlNQ3" })}
          count={government.feedCount}
          active={tab === "connect"}
          dataIntercomTarget={government.viewerIsMember ? "Government Activity Tab" : null}
        />
      )}

      <NewLinkTab
        href="/governments/[governmentSlug]/[[...content]]"
        as={`/governments/${government.slug}/projects`}
        title={intl.formatMessage({ defaultMessage: "Projects", id: "UxTJRa" })}
        count={government.projectsCount}
        active={tab === "projects"}
        dataIntercomTarget={government.viewerIsMember ? "Government Projects Tab" : null}
      />

      <NewLinkTab
        href="/governments/[governmentSlug]/[[...content]]"
        as={`/governments/${government.slug}/products`}
        title={intl.formatMessage({ defaultMessage: "Products", id: "7NFfmz" })}
        count={government.viewerCanSeeProductStack ? government.productsCount : 0}
        active={tab === "products"}
        dataIntercomTarget={government.viewerIsMember ? "Government Products Tab" : null}
      />

      {!!government.storiesCount && government.storiesCount > 0 && (
        <NewLinkTab
          href="/governments/[governmentSlug]/[[...content]]"
          as={`/governments/${government.slug}/stories`}
          title={intl.formatMessage({ defaultMessage: "Stories", id: "Ldxym4" })}
          count={government.storiesCount}
          active={tab === "stories"}
          dataIntercomTarget={government.viewerIsMember ? "Government Stories Tab" : null}
        />
      )}

      {government.viewerIsMember && (
        <NewLinkTab
          href="/governments/[governmentSlug]/[[...content]]"
          as={`/governments/${government.slug}/actions`}
          title={intl.formatMessage({ defaultMessage: "Actions", id: "wL7VAE" })}
          count={government.actionsCount}
          active={tab === "actions"}
          dataIntercomTarget={government.viewerIsMember ? "Government Actions Tab" : null}
        />
      )}

      {government.viewerIsMember && (
        <NewLinkTab
          href="/governments/[governmentSlug]/[[...content]]"
          as={`/governments/${government.slug}/settings`}
          title={intl.formatMessage({ defaultMessage: "Settings", id: "D3idYv" })}
          count={0}
          active={tab === "settings"}
          css={mediaQueries({
            marginLeft: [0, 0, 0, "auto"],
            borderLeft: [0, 0, 0, `1px solid ${palette.lightestGray}`],
          })}
          dataIntercomTarget={government.viewerIsMember ? "Government Settings Tab" : null}
        />
      )}
    </ProfileNavbar>
  );
}

export default GovernmentPage;
